import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FIELDS = [
  ["name", "Your full name *", "text"], ["mobile", "Mobile number *", "tel"],
  ["email", "Email *", "email"], ["password", "Password (min 6 chars) *", "password"],
  ["business_name", "Business name *", "text"], ["city", "City", "text"],
  ["state", "State", "text"], ["pincode", "Warehouse PIN code *", "text"], ["gstin", "GSTIN (optional)", "text"], ["pan", "PAN (optional)", "text"],
];

export default function Register() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/register-agent", form);
      const me = await api.get("/auth/me");
      setUser(me.data);
      navigate("/agent");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F6FB] py-10 px-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div className="mx-auto max-w-lg">
        <div className="mb-8 text-center">
          <Link to="/"><img src="/logo.png" alt="DropPartner" className="mx-auto h-16 w-auto rounded-2xl bg-white object-contain p-2 shadow-md" /></Link>
          <h1 className="mt-5 text-2xl font-extrabold tracking-tight text-[#0F2440]" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="register-title">
            Start your online business
          </h1>
          <p className="mt-1 text-sm text-slate-500">Create your seller account — choose a plan on the next step.</p>
        </div>
        <motion.form initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit}
          className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_20px_50px_rgba(15,42,92,0.08)] sm:p-8" data-testid="register-form">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {FIELDS.map(([k, label, type]) => (
              <div key={k} className={["name", "email", "business_name", "password"].includes(k) ? "" : ""}>
                <Label className="text-xs font-medium text-slate-500">{label}</Label>
                <Input data-testid={`register-${k}`} type={type} value={form[k] || ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  required={label.includes("*")}
                  className="mt-1.5 border-slate-200 bg-white text-[#0F2440] focus-visible:ring-[#FF6B00]" />
              </div>
            ))}
          </div>
          <div className="mt-4">
            <Label className="text-xs font-medium text-slate-500">Address</Label>
            <Input data-testid="register-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="mt-1.5 border-slate-200 bg-white text-[#0F2440]" />
          </div>
          {error && <p data-testid="register-error" className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-600">{error}</p>}
          <Button data-testid="register-submit-btn" disabled={busy}
            className="mt-6 w-full bg-[#FF6B00] py-6 text-sm font-bold text-white shadow-lg transition-all hover:bg-[#E85D04]">
            {busy ? "Creating your account…" : <>CREATE ACCOUNT <ArrowRight size={15} className="ml-1" /></>}
          </Button>
          <p className="mt-4 text-center text-xs text-slate-500">
            Already have an account? <Link to="/login" data-testid="register-login-link" className="font-semibold text-[#1D5BD8] hover:text-[#FF6B00]">Sign in</Link>
          </p>
        </motion.form>
      </div>
    </div>
  );
}
